// Help-system telemetry — fire-and-forget writes to help_events
// (mig 20260704172951). Feeds the platform-admin deflection dashboard.
//
// Hard rules (plan §8): this must NEVER block, throw, queue, or compete with
// the operational offline queue (clock-ins, QC captures own the tight iOS
// origin cap). So:
//   - dropped entirely when offline — telemetry is not worth a queue slot;
//   - not awaited; DB/network errors are swallowed (a lost telemetry row is
//     worthless to chase and must never reach the operator);
//   - tenant_id / user_id default server-side (current_tenant_id() /
//     auth.uid()), so a plain insert carries no PII the caller has to thread.

import { supabase } from '@/lib/supabase'

export type HelpEventType =
  | 'drawer_open' | 'search' | 'zero_result' | 'video_play' | 'download_offline'
  // Ask BigAgri (mig 20260721170000): asked/answered/link-tap/error — the
  // link tap is the outcome signal the spec ranks the feature by.
  | 'ask_asked' | 'ask_answered' | 'ask_link_tap' | 'ask_error' | 'ask_answer'
  // ask_retry (mig 20260722100000): the user pressed retry after a failure —
  // the retry rate is the trust metric the error copy is judged by.
  | 'ask_retry'
  // ask_abort (mig 20260804171000): a live stream was deliberately killed
  // (barge-in, left the page) before done/error — abandonment is a signal too.
  | 'ask_abort'

// query_text is only meaningful (and only stored) for these events — the
// DB CHECK caps it at 200 chars; we mirror that here and never send it for
// drawer_open etc. (POPIA data-minimisation). For ask_asked / ask_retry it is
// the question itself (the unanswerable ones are the next tools to build);
// for ask_answered it is the compact "<ms>ms/<rounds>r/cr<n>/cw<n>" latency +
// cache stamp (cr/cw omitted when the gateway predates them); for
// ask_error it is the failure stage code ("http_502", "net", …); for
// ask_abort it is the "<ms>ms/<chars>ch" run-length stamp; for
// ask_link_tap it is the tapped app route — no PII.
const STORES_QUERY_TEXT = new Set<HelpEventType>(['search', 'zero_result', 'ask_asked', 'ask_answered', 'ask_error', 'ask_answer', 'ask_retry', 'ask_link_tap', 'ask_abort'])

export interface HelpEventInput {
  eventType:    HelpEventType
  route?:       string
  helpTopicId?: string
  queryText?:   string
}

export function logHelpEvent(input: HelpEventInput): void {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return
  const row = {
    event_type:    input.eventType,
    route:         input.route?.slice(0, 200) ?? null,
    help_topic_id: input.helpTopicId?.slice(0, 100) ?? null,
    query_text:    STORES_QUERY_TEXT.has(input.eventType)
      ? (input.queryText?.trim().slice(0, 200) || null)
      : null,
  }
  // No .select() — help_events is INSERT-only under RLS (no SELECT grant for
  // non-admins). Swallow both resolution paths so nothing ever surfaces.
  void supabase.from('help_events').insert(row).then(() => {}, () => {})
}
